import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Client } from '../../../api/app-types/client.app';
import { CLIENT_API } from '../../../api/injection-tokens/services.token';
import { provideClientApi } from '../../../api/services/client.api';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-client-anonymize-dialog',
	template: `
		<h2 mat-dialog-title>{{ 'client.delete.title' | transloco }}</h2>
		@if (canBeDeleted() === false) {
			<mat-dialog-content>
				<p>
					{{ 'client.delete.remainingAssignment' | transloco: { firstName: client.firstName, lastName: client.name } }}
				</p>
			</mat-dialog-content>
			<mat-dialog-actions align="end">
				<button matButton="filled" class="lvr-secondary" mat-dialog-close>{{ 'client.delete.ok' | transloco }}</button>
			</mat-dialog-actions>
		} @else {
			<mat-dialog-content>
				<p>
					{{ 'client.delete.confirmation' | transloco: { firstName: client.firstName, lastName: client.name } }}
				</p>
			</mat-dialog-content>
			<mat-dialog-actions class="actions-row">
				<lvr-button
					variant="secondary"
					[label]="'base.button.cancel' | transloco"
					[type]="'button'"
					icon="disabled_by_default"
					mat-dialog-close
				></lvr-button>
				<lvr-button
					variant="primary"
					[label]="'client.delete.delete' | transloco"
					[loadingLabel]="'client.delete.deleting' | transloco"
					[type]="'button'"
					[loading]="loading()"
					[disabled]="loading()"
					(clicked)="deleteClient()"
					icon="delete"
				></lvr-button>
			</mat-dialog-actions>
		}
	`,
	imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose, MatButton, TranslocoPipe, LvrButton],
	providers: [provideClientApi],
})
export class ClientDeleteDialog implements OnInit {
	public readonly client: Client = inject(MAT_DIALOG_DATA);
	private readonly dialogRef = inject(MatDialogRef<ClientDeleteDialog>);
	private readonly clientService = inject(CLIENT_API);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);

	public readonly loading = signal(false);
	public readonly canBeDeleted = signal(false);

	public ngOnInit() {
		this.canClientBeDeleted();
	}

	public async canClientBeDeleted() {
		const result = await this.clientService.getCanClientBeDeleted(this.client.id);
		this.canBeDeleted.set(result);
	}

	public async deleteClient() {
		if (this.loading()) return;

		if (!this.canBeDeleted()) {
			return;
		}

		this.loading.set(true);
		try {
			await this.clientService.delete(this.client.id);
			this.ntfySrv.addNotification(
				createSuccessNotification(
					this.transloco.translate('base.notification_message.client_anonymized', {
						firstName: this.client.firstName,
						lastName: this.client.name,
					}),
				),
			);
			this.dialogRef.close(true);
		} catch {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.client_anonymize_error')),
			);
		} finally {
			this.loading.set(false);
		}
	}
}
